/* eslint-disable quotes */
import classNames from 'classnames';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, {Fragment, memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';

// Components
import LineArrow from 'Components/flowChart/components/LineArrow';
import ImageIcon from 'Components/ImageIcon';
import Icon from '../icon';
import AddNodeButton from './components/AddNodeButton';
import CopyBranchButton from './components/CopyBranchButton';
import NodePreview from './components/NodePreview';
import PreviewData from './components/PreviewData';
import ComponentUITippy from './components/Tippy';

// Styles
import {
	Badge,
	BadgePercent,
	BadgeTotal,
	IconDelete,
	Label,
	NoData,
	Node,
	NodeBorder,
	StyledBranch,
	StyledEntry,
	StyledFlowChart,
	Text,
	WrapperIcon,
	WrapperLabel,
	WrapperNode,
	WrapperPreview
} from './index.style';

// useReducer
import {actionTypes} from 'Components/flowChart/useReducer';

// Constant
import {configs, MODE, NODE_TYPE} from 'Components/flowChart/constant';

// Assets
import {getObjectPropSafely, random} from 'Utils';

// Utils
import {
	checkViolationByRules,
	checkIsBranch,
	checkIsCopyable,
	checkIsHiddenBadge,
	checkIsNodeIfThen,
	checkIsNodeIfThenParent,
	checkIsSmallSpacingBranch,
	checkIsSmallSpacingNodeIfThen,
	findBranch
} from './utils';

const PATH = 'Src/Components/flowChart/index.js';

const renderIconContent = (icon) => (React.isValidElement(icon) ? icon : <Icon type={icon} />);

function FlowChart(props) {
	// console.log({props});

	const {
		getTranslateMessage,
		// titleNodes,
		UITippy,
		logs,
		defaultDataPreview,
		allowCopyBranch = true,
		mode = MODE.EDIT,
		draggingNode,
		timezone,
		dataListing
	} = props;
	const {state, useDispatch} = props;
	const isViewMode = mode === MODE.VIEW;

	const ref = useRef();
	const [hoverNodeId, setHoverNode] = useState();

	const firstNode = useMemo(() => {
		try {
			if (state.nodeTrees && state.nodeTrees[0] && state.nodeTrees.length) {
				return state.nodeTrees[0];
			}
		} catch (e) {
			props.onError(e, {
				component: PATH,
				action: 'firstNode',
				args: {}
			});
		}
	}, [state.nodeTrees]);

	useEffect(() => {
		try {
			const getNodes = state.flattenNodes.length ? [].concat(getChildrenNodes(null, state.flattenNodes)) : [];

			props.callback('SET_NODE_TREES', getNodes);
			useDispatch({
				type: actionTypes.SET_NODE_TREES,
				payload: {
					nodeTrees: getNodes
				}
			});

			if (state.flattenNodes.some((node) => node.type === configs.NODE_TYPE_GO_TO_OTHER_ACTION)) {
				let arrActionGoToOtherAction = [];

				for (let i = 0, length = state.flattenNodes.length; i < length; i++) {
					let node = state.flattenNodes[i];

					if (node.type === configs.NODE_TYPE_GO_TO_OTHER_ACTION) {
						arrActionGoToOtherAction = [...arrActionGoToOtherAction, [node.nodeId, node.objectRefId]];
					}
				}

				useDispatch({
					type: actionTypes.SET_ARRAY_ACTION_GO_TO_OTHER_ACTION,
					payload: {
						arrActionGoToOtherAction
					}
				});
			}

			callbackNode(state.flattenNodes);
		} catch (e) {
			props.onError(e, {
				component: PATH,
				action: 'useEffect -> setNodeTrees',
				args: {}
			});
		}
	}, [state.flattenNodes]);

	useEffect(() => {
		try {
			const getNodes = props.data.length ? [].concat(getChildrenNodes(null, props.data)) : [];

			props.callback('SET_NODE_TREES', getNodes);
			useDispatch({
				type: actionTypes.SET_NODE_TREES,
				payload: {
					nodeTrees: getNodes
				}
			});
			callbackNode(props.data);
		} catch (e) {
			props.onError(e, {
				component: PATH,
				action: 'useEffect -> setNodeTrees',
				args: {}
			});
		}
	}, [props.renderKey]);

	useEffect(() => {
		try {
			let newData = [];

			if (props.data && typeof props.data !== 'undefined' && props.data.length) {
				newData = props.data;
			}

			useDispatch({
				type: actionTypes.SET_FLATTEN_NODES,
				payload: {
					flattenNodes: newData
				}
			});
		} catch (e) {
			props.onError(e, {
				component: PATH,
				action: 'useEffect -> setFlattenNodes',
				args: {}
			});
		}
	}, [props.data]);

	// This use effect run when drop one node in to create node
	useEffect(() => {
		try {
			const {id = '', droppableId = ''} = props.dropNode;
			const node = props.nodes.find((node) => node.key === id);

			if (node) {
				const lengthNodes = getObjectPropSafely(() => state.flattenNodes.length, 0);

				const checkIsViolateFn = checkViolationByRules({
					rules: props.rules,
					data: state.flattenNodes,
					nodeBefore: {id: droppableId}
				});

				if (!checkIsViolateFn(node)) {
					let param = [];

					param = {
						icon: node.icon,
						label: node.label,
						nodeId: random(5),
						parentId: lengthNodes > 0 ? droppableId : null,
						active: true,
						moreInfo: node.moreInfo || {},
						...node
					};

					// createNode(param);
					createNodeV2(param);
				}
			}
		} catch (e) {
			props.onError(e, {
				component: PATH,
				action: 'useEffect -> handle drop node',
				args: {}
			});
		}
	}, [props.dropNode]);

	const onMouseOverNode = (nodeId) => () => {
		setHoverNode(nodeId);
	};

	const onMouseOutNode = () => {
		setHoverNode('');
	};

	const renderLayoutHover = (nodeId) => {
		if (state.statusGoToOtherAction.isSelect) {
			if (
				state.statusGoToOtherAction.nodeIdToGo !== nodeId &&
				!state.disabledNodes.includes(nodeId) &&
				state.statusGoToOtherAction.nodeIdToGo !== nodeId
			) {
				return (
					<div
						onClick={() => handleGoToOtherAction(nodeId)}
						style={{position: 'absolute', width: '100%', height: '100%', zIndex: '10'}}
						onMouseOver={onMouseOverNode(nodeId)}
						onMouseOut={onMouseOutNode}
					/>
				);
			}
		}
	};

	const callbackNode = (node) => {
		try {
			if (node && typeof props.callback === 'function') {
				props.callback('CALLBACK_NODE', node);
			}
		} catch (e) {
			props.onError(e, {
				component: PATH,
				action: 'callbackNode',
				args: {node}
			});
		}
	};

	const callbackOnActiveNode = (params, index) => {
		try {
			if (params && Array.isArray(params) && typeof props.onActiveNode === 'function') {
				if (typeof index !== 'undefined') {
					props.onActiveNode(params[index]);
				} else {
					let nodeActive = params.find(
						(param) =>
							param.type === configs.NODE_TYPE_IF_THEN ||
							param.type === configs.NODE_TYPE_IF_THEN_PARALLEL
					);

					if (!nodeActive) {
						nodeActive = params[0];
					}
					props.onActiveNode(nodeActive);
				}
			}
		} catch (e) {
			props.onError(e, {
				component: PATH,
				action: 'callbackOnActiveNode',
				args: {params}
			});
		}
	};

	const handleGoToOtherAction = (nodeId) => {
		try {
			useDispatch({
				type: actionTypes.SET_REF_OBJECT_ID_GO_TO_OTHER_ACTION,
				payload: {
					objectRefId: nodeId,
					nodeId: state.statusGoToOtherAction.nodeIdToGo
				}
			});
			useDispatch({
				type: actionTypes.CANCEL_CHANGE_NODE_GO_TO_OTHER_ACTION
			});
		} catch (error) {
			props.onError(error, {
				path: PATH,
				action: 'handleGoToOtherAction',
				args: {}
			});
		}
	};

	const onClickNode = (node) => {
		try {
			if (node) {
				if (state.statusGoToOtherAction.isSelect) {
					if (
						state.statusGoToOtherAction.nodeIdToGo !== node.nodeId &&
						!state.disabledNodes.includes(node.nodeId)
					) {
						handleGoToOtherAction(node);
					}
				} else {
					const nodeId = node.nodeId;
					const nodeIndex = state.flattenNodes.findIndex((flattenItem) => flattenItem.nodeId === nodeId);

					callbackOnActiveNode(state.flattenNodes, nodeIndex);
				}
			}
		} catch (e) {
			props.onError(e, {
				component: PATH,
				action: 'onClickNode',
				args: {node}
			});
		}
	};

	const onClickRemoveNode = (evt, node) => {
		try {
			evt.stopPropagation();
			if (node) {
				useDispatch({
					type: actionTypes.REMOVE_NODE,
					payload: node
				});
			}
		} catch (e) {
			props.onError(e, {
				component: PATH,
				action: 'onClickRemoveNode',
				args: {node}
			});
		}
	};

	const callbackCopyBranch = useCallback((evt, params) => {
		try {
			onClickCopyBranch(evt, params);
		} catch (e) {
			props.onError(e, {
				component: PATH,
				action: 'callbackAddNodeButton',
				args: {
					evt,
					params
				}
			});
		}
	});

	const onClickCopyBranch = (evt, node) => {
		try {
			evt.stopPropagation();
			if (node) {
				useDispatch({
					type: actionTypes.COPY_BRANCH,
					payload: node
				});
			}
		} catch (e) {
			props.onError(e, {
				component: PATH,
				action: 'onClickCopyBranch',
				args: {node}
			});
		}
	};

	const lookupData = useMemo(() => {
		const events = getObjectPropSafely(() => _.keyBy(dataListing.events, 'eventTrackingName'), {});
		const destinations = getObjectPropSafely(() => _.keyBy(dataListing.destinations, 'destination_id'), {});

		return {
			events,
			destinations
		};
	}, [dataListing]);

	const renderNode = (node, key, parentBranchLength) => {
		try {
			let result = null;
			let disabledRemove = props.disabledRemoveNodes[node.nodeId] || false;

			const nodeReadOnly =
				node.type === 'WAIT_EVENT_HAPPENED' ||
				node.type === 'WAIT_EVENT_TIMEOUT' ||
				node.type === 'ALLOCATE_COUPON_SUCCESS' ||
				node.type === 'ALLOCATE_COUPON_NOT_ENOUGH' ||
				node.type === 'ALLOCATE_COUPON_NOT_MATCH';

			if (node) {
				const isNodeIfThen = checkIsNodeIfThen(node.type);
				const isParentNodeIfThen = checkIsNodeIfThenParent(node.type);
				const isBranch = checkIsBranch(node.type);
				const isCopyable = checkIsCopyable(node.type);
				const isHiddenBadge = checkIsHiddenBadge(node.type);
				const isSmallSpacing = checkIsSmallSpacingNodeIfThen(node.type, mode);
				const isSmallSpacingBranch = checkIsSmallSpacingBranch(node.type, mode);
				const isNodeGoto = node.type === configs.NODE_TYPE_GO_TO_OTHER_ACTION;
				const disabled = state.disabledNodes.includes(node.nodeId);
				const moreInfo = node.moreInfo || {};
				const isDisableActionBranch = node.isDisableActionBranch || false;
				const isActive =
					props.type === 'multi-check'
						? props.activeNodeMulti.some((each) => each.nodeId === node.nodeId)
						: node.nodeId === props.activeNode.nodeId;

				if (['CONDITION_YES', 'WAIT_FOR_RESPONSE_NODE'].includes(node.type)) {
					disabledRemove = parentBranchLength === 2;
				} else if (
					[
						'AB_SPLIT_NODE',
						'AB_SPLIT_CONTROL_NODE',
						'WAIT_EVENT_HAPPENED',
						'WAIT_EVENT_TIMEOUT',
						'CONDITION_NO',
						'WAIT_FOR_RESPONSE_NODE_NO',
						'ALLOCATE_COUPON_SUCCESS',
						'ALLOCATE_COUPON_NOT_ENOUGH',
						'ALLOCATE_COUPON_NOT_MATCH'
					].includes(node.type)
				) {
					disabledRemove = true;
				}

				let disabledCopy = false;

				if (isBranch) {
					const parentNode = findBranch(state.nodeTrees[0], node.parentId);

					if (parentNode?.branchs?.length >= 20) {
						disabledCopy = true;
					}
				}

				let label = node.label;

				// if (isViewMode && node.type === NODE_TYPE.AB_SPLIT_NODE && moreInfo?.groupPercent) {
				// 	label = `${label} (${moreInfo.groupPercent})`;
				// }

				let audienceTotal, audiencePercent;

				if (moreInfo.audience) {
					const audienceInfo = moreInfo.audience.split(' - ');

					audienceTotal = audienceInfo[0] || '0';
					audiencePercent = audienceInfo[1] || '0%';
				}
				const customStyles = _.pick(moreInfo, [
					'backgroundColor',
					'color',
					'hoverColor',
					'hoverBorder',
					'activeColor',
					'activeBorder'
				]);

				const handleClick = () => {
					if (disabled || nodeReadOnly) {
						return;
					}

					onClickNode(node);
				};

				result = (
					<StyledEntry
						className={`${isNodeIfThen ? '' : 'sole'}`}
						use={props.use}
						key={node.nodeId}
						isBranch={isBranch}
						isSmallSpacing={isSmallSpacingBranch}
						draggingNode={draggingNode}
						isViewMode={isViewMode}
					>
						<WrapperNode isViewMode={isViewMode}>
							<Node
								id={'node_' + node.nodeId}
								isNotFlowResume={moreInfo.isNotFlowResume}
								backgroundColor={moreInfo.backgroundColor}
							>
								{renderLayoutHover(node.nodeId)}
								<PreviewData
									use={props.use}
									defaultDataPreview={defaultDataPreview}
									UITippy={UITippy}
									data={logs[node.nodeId]}
								/>
								<div
									className={classNames(
										'label-node',
										{error: props.errors[node.nodeId]},
										{'node-preview': props.use === 'preview'},
										{
											'node-preview-data':
												props.use === 'preview-data' || props.use === 'preview-data-expanded'
										},
										{disabled: disabled},
										{'read-only': nodeReadOnly}
									)}
									onClick={handleClick}
								>
									<WrapperIcon
										className={classNames({
											active: isActive,
											error: props.errors[node.nodeId]
										})}
										nodeType={node.type}
										customStyles={customStyles}
									>
										{node.icon ? (
											renderIconContent(node.icon)
										) : (
											<>
												<ImageIcon src={node.iconUrl} />
												<NodeBorder />
											</>
										)}
									</WrapperIcon>

									{!disabledRemove && !isViewMode && (
										<IconDelete
											className="icon-antsomi-close"
											onClick={(e) => onClickRemoveNode(e, node)}
										/>
									)}
								</div>

								<UITippy content={label}>
									<WrapperLabel
										className={classNames({
											active: isActive,
											error: props.errors[node.nodeId]
										})}
										isViewMode={isViewMode}
										nodeType={node.type}
										customStyles={customStyles}
										onClick={handleClick}
									>
										<Label isViewMode={isViewMode}>{label}</Label>
										{moreInfo.groupPercent ? (
											<span className="group-percent">{`(${moreInfo.groupPercent})`}</span>
										) : null}
									</WrapperLabel>
								</UITippy>
							</Node>

							{isViewMode && (
								<WrapperPreview>
									<NodePreview
										node={node}
										flattenNodes={flattenNodes}
										onError={props.onError}
										timezone={timezone}
										lookupData={lookupData}
									/>
									{moreInfo.audience && !isHiddenBadge ? (
										<Badge>
											<Icon type="icon-antsomi-account-circle" color="#81BCF4" fontSize="14px" />
											<BadgeTotal>{audienceTotal}</BadgeTotal>
											<BadgePercent>{audiencePercent}</BadgePercent>
										</Badge>
									) : null}
								</WrapperPreview>
							)}
						</WrapperNode>

						{isCopyable && !isViewMode ? (
							<StyledEntry
								className={`sole-copy start-node`}
								use={props.use}
								draggingNode={draggingNode}
								isViewMode={isViewMode}
							>
								<CopyBranchButton
									allowCopyBranch={allowCopyBranch}
									use={props.use}
									disabledCopy={disabledCopy}
									data={state.flattenNodes}
									nodeId={node.nodeId}
									callback={callbackCopyBranch}
									getTranslateMessage={getTranslateMessage}
									node={node}
									isViewMode={isViewMode}
								/>
							</StyledEntry>
						) : null}

						{!isParentNodeIfThen && !isNodeGoto && !isViewMode && !isDisableActionBranch ? (
							<StyledEntry
								className={`sole end-node`}
								use={props.use}
								draggingNode={draggingNode}
								isViewMode={isViewMode}
							>
								<AddNodeButton
									allowAddNode={props.allowAddNode}
									use={props.use}
									rules={props.rules}
									data={state.flattenNodes}
									nodeData={props.nodes}
									draggingNode={draggingNode}
									nodeId={node.nodeId}
									parentId={node.nodeId}
									callback={callbackAddNodeButton}
									tableNodes={props.tableNodes}
									getTranslateMessage={getTranslateMessage}
									isViewMode={isViewMode}
									containerSelector={props.containerSelector}
								/>
							</StyledEntry>
						) : null}

						<StyledBranch
							isSmallSpacing={isSmallSpacing}
							isParentNodeIfThen={isParentNodeIfThen}
							isViewMode={isViewMode}
						>
							{node.branchs && node.branchs.length ? (
								node.branchs.map((branch, idx) =>
									renderNode(
										branch,
										`${branch.parentId}-${branch.nodeId}-${idx}`,
										node.branchs.length
									)
								)
							) : (
								<StyledEntry
									className={`sole flag`}
									use={props.use}
									draggingNode={draggingNode}
									isViewMode={isViewMode}
								>
									<WrapperIcon nodeType={NODE_TYPE.END}>
										<Icon type="icon-antsomi-flag" />
									</WrapperIcon>
								</StyledEntry>
							)}
						</StyledBranch>
					</StyledEntry>
				);
			}

			return result;
		} catch (e) {
			props.onError(e, {
				component: PATH,
				action: 'renderNode',
				args: {
					node,
					key
				}
			});
		}
	};

	const renderNodes = (parentNode, nodes, firstNode) => {
		try {
			if (nodes && nodes.length) {
				return nodes.map((node) => {
					const isNodeIfThen = checkIsNodeIfThen(node.type);

					return (
						<Fragment key={node.nodeId}>
							<StyledBranch firstNode={firstNode} isViewMode={isViewMode}>
								{node.branchs && node.branchs.length ? (
									node.branchs.map((branch) =>
										renderNode(branch, `${branch.parentId}-${branch.nodeId}`, node.branchs.length)
									)
								) : (
									<StyledEntry
										className={`${isNodeIfThen ? 'flag' : 'sole flag'}`}
										use={props.use}
										draggingNode={draggingNode}
										isViewMode={isViewMode}
									>
										<WrapperIcon nodeType={NODE_TYPE.END}>
											<Icon type="icon-antsomi-flag" />
										</WrapperIcon>
									</StyledEntry>
								)}
							</StyledBranch>
						</Fragment>
					);
				});
			}
		} catch (e) {
			props.onError(e, {
				component: PATH,
				action: 'renderNodes',
				args: {
					parentNode,
					nodes
				}
			});
		}
	};

	const callbackAddNodeButton = useCallback((params) => {
		try {
			createNodeV2(params);
		} catch (e) {
			props.onError(e, {
				component: PATH,
				action: 'callbackAddNodeButton',
				args: {
					params
				}
			});
		}
	});

	const createNodeV2 = (node) => {
		try {
			if (node && props.allowAddNode) {
				useDispatch({
					type: actionTypes.CREATE_NODE,
					payload: {
						node
					}
				});
			}
		} catch (e) {
			props.onError(e, {
				component: PATH,
				action: 'createNode',
				args: {
					params: node
				}
			});
		}
	};

	const getChildrenNodes = (node, nodes) => {
		try {
			if (node && node.nodeId && nodes.length) {
				let arrChildren = [];

				for (let i = 0; i < nodes.length; i++) {
					if (node.nodeId === nodes[i].parentId) {
						let children = {...nodes[i]};

						children.branchs = getChildrenNodes({...nodes[i]}, nodes);

						arrChildren.push({
							...children
						});
					}
				}

				if (arrChildren.length) {
					return arrChildren;
				} else {
					return false;
				}
			} else if (!node) {
				for (let i = 0; i < nodes.length; i++) {
					if (!nodes[i].parentId) {
						let nodeInfo = {...nodes[i]};
						let arrChildren = getChildrenNodes(nodeInfo, nodes);

						if (arrChildren && arrChildren.length) {
							return {
								...nodeInfo,
								branchs: arrChildren
							};
						} else {
							return {
								...nodeInfo
							};
						}
					}
				}
			}
		} catch (e) {
			props.onError(e, {
				component: PATH,
				action: 'getChildrenNodes',
				args: {
					node,
					nodes
				}
			});
		}
	};

	const flattenNodes = useMemo(() => {
		return getObjectPropSafely(() => _.keyBy(state.flattenNodes, 'nodeId'), {});
	}, [state.flattenNodes]);

	const renderFirstNode = () => {
		const moreInfo = firstNode.moreInfo || {};
		const isActive =
			props.type === 'multi-check'
				? props.activeNodeMulti.some((each) => each.nodeId === firstNode.nodeId)
				: firstNode.nodeId === props.activeNode.nodeId;

		let label = firstNode.label;

		// if (isViewMode && firstNode.type === NODE_TYPE.AB_SPLIT_NODE && moreInfo.groupPercent) {
		// 	label = `${label} (${moreInfo.groupPercent})`;
		// }

		let audienceTotal, audiencePercent;

		if (moreInfo.audience) {
			const audienceInfo = moreInfo.audience.split(' - ');

			audienceTotal = audienceInfo[0] || '0';
			audiencePercent = audienceInfo[1] || '0%';
		}

		const customStyles = _.pick(moreInfo, [
			'backgroundColor',
			'color',
			'hoverColor',
			'hoverBorder',
			'activeColor',
			'activeBorder'
		]);

		const handleClick = () => {
			if (state.disabledNodes.includes(firstNode.nodeId)) {
				return;
			}

			onClickNode(firstNode);
		};

		return (
			<WrapperNode isViewMode={isViewMode} firstNode>
				<Node
					id={'node_' + firstNode.nodeId}
					firstNode
					use={props.use}
					isNotFlowResume={moreInfo.isNotFlowResume}
				>
					<PreviewData
						firstNode
						use={props.use}
						defaultDataPreview={defaultDataPreview}
						UITippy={UITippy}
						data={logs[firstNode.nodeId]}
					/>
					<div
						className={classNames(
							'label-node',
							{error: props.errors[firstNode.nodeId]},
							{disabled: state.disabledNodes.includes(firstNode.nodeId)},
							{'node-preview': props.use === 'preview'},
							{
								'node-preview-data':
									props.use === 'preview-data' || props.use === 'preview-data-expanded'
							}
						)}
						onClick={handleClick}
					>
						<WrapperIcon
							firstNode
							className={classNames({
								active: isActive,
								error: props.errors[firstNode.nodeId]
							})}
							nodeType={firstNode.type}
							customStyles={customStyles}
						>
							{firstNode.icon ? renderIconContent(firstNode.icon) : <ImageIcon src={firstNode.iconUrl} />}
						</WrapperIcon>

						{props.disabledRemoveNodes[firstNode.nodeId] === undefined && mode !== MODE.VIEW && (
							<IconDelete
								className="icon-antsomi-close"
								onClick={(e) => onClickRemoveNode(e, firstNode)}
							/>
						)}
					</div>
					<UITippy content={label}>
						<WrapperLabel
							className={classNames({
								active: isActive,
								error: props.errors[firstNode.nodeId]
							})}
							isViewMode={isViewMode}
							nodeType={firstNode.type}
							customStyles={customStyles}
							onClick={handleClick}
						>
							<Label isViewMode={isViewMode}>{label}</Label>
							{moreInfo.groupPercent ? (
								<span className="group-percent">{`(${moreInfo.groupPercent})`}</span>
							) : null}
						</WrapperLabel>
					</UITippy>
				</Node>
				{isViewMode && (
					<WrapperPreview>
						<NodePreview
							firstNode
							node={firstNode}
							flattenNodes={flattenNodes}
							onError={props.onError}
							timezone={timezone}
							lookupData={lookupData}
						/>
						{moreInfo.audience ? (
							<Badge>
								<Icon type="icon-antsomi-account-circle" color="#81BCF4" fontSize="14px" />
								<BadgeTotal>{audienceTotal}</BadgeTotal>
								<BadgePercent>{audiencePercent}</BadgePercent>
							</Badge>
						) : null}
					</WrapperPreview>
				)}
				<StyledEntry
					className={`sole end-node`}
					use={props.use}
					draggingNode={draggingNode}
					firstNode
					isViewMode={isViewMode}
				>
					{!isViewMode && (
						<AddNodeButton
							allowAddNode={props.allowAddNode}
							use={props.use}
							rules={props.rules}
							data={state.flattenNodes}
							nodeData={props.nodes}
							nodeId={firstNode.nodeId}
							parentId={firstNode.nodeId}
							tableNodes={props.tableNodes}
							callback={callbackAddNodeButton}
							getTranslateMessage={getTranslateMessage}
							draggingNode={draggingNode}
							isViewMode={isViewMode}
							containerSelector={props.containerSelector}
						/>
					)}
				</StyledEntry>
				{state.nodeTrees && state.nodeTrees.length ? renderNodes(null, state.nodeTrees, true) : null}
			</WrapperNode>
		);
	};

	try {
		return (
			<>
				<StyledFlowChart className="dataflow-chart" use={props.use} ref={ref}>
					<LineArrow
						hoverNodeId={hoverNodeId}
						arrActionGoToOtherAction={state.arrActionGoToOtherAction}
						statusGoToOtherAction={state.statusGoToOtherAction}
					/>
					{firstNode && Object.keys(firstNode).length ? (
						renderFirstNode()
					) : (
						<NoData>
							<AddNodeButton
								allowAddNode={props.allowAddNode}
								use={props.use}
								tableNodes={props.tableNodes}
								nodeData={props.nodes}
								callback={callbackAddNodeButton}
								rules={props.rules}
								nodeId="node-no-data"
								droppableStyle={{left: -20, width: 300}}
								getTranslateMessage={getTranslateMessage}
								draggingNode={draggingNode}
								isViewMode={isViewMode}
								containerSelector={props.containerSelector}
							/>
							<Text>{getTranslateMessage('_', 'Start with a trigger node')}</Text>
						</NoData>
					)}
				</StyledFlowChart>
			</>
		);
	} catch (e) {
		props.onError(e, {
			component: PATH,
			action: 'render -> FlowChart',
			args: {}
		});
	}
}

const propTypes = {
	logs: PropTypes.object,
	rules: PropTypes.object,
	onError: PropTypes.func,
	onValidate: PropTypes.func,
	use: PropTypes.string
};

const defaultProps = {
	rules: {},
	errors: {},
	logs: {},
	disabledRemoveNodes: {},
	titleNodes: {},
	allowAddNode: true,
	initData: [],
	// use: 'preview-data',
	// use: 'preview-data-expanded',
	use: 'design',
	onError: (args) => {
		console.error(args);
	},
	// onValidate: () => {},
	UITippy: ComponentUITippy
};

FlowChart.defaultProps = defaultProps;
FlowChart.propTypes = propTypes;

export default memo(FlowChart);
